/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 04.11.17.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";

import ActionWizard from "../../containers/ActionWizard";
import {t} from "../../trans";
import StepChangePassword from "./StepChangePassword";
import StepShowQrCode from "./StepShowQrCode";
import {SessionStore} from "../../services/Session";
import StepAuthenticate from "./StepAuthenticate";

let stepsResetPassword = [
    {
        title: t("Please change password"),
        component: StepChangePassword,
        forwardButton: t("Change password"),
        props: {}
    }
];

let stepsResetKey = [
    {
        title: t("Set up Authenticator"),
        component: StepShowQrCode,
        forwardButton: t("Next")
    },
    {
        title: t("Enter code"),
        component: StepChangePassword,
        backButton: t("Back"),
        forwardButton: t("Verify code")
    }
];

let stepsResetAll = [
    {
        title: t("Set up Authenticator"),
        component: StepShowQrCode,
        forwardButton: t("Next")
    },
    {
        title: t("Please change password"),
        component: StepChangePassword,
        backButton: t("Back"),
        forwardButton: t("Change password"),
        props: {}
    }
];

let stepsChangePassword = [
    {
        title: t("Please authenticate"),
        component: StepAuthenticate,
        forwardButton: t("Authenticate")
    },
    {
        title: t("Change password"),
        component: StepChangePassword,
        forwardButton: t("Change password"),
        props: {}
    }
];

let stepsChangeKey = [
    {
        title: t("Please authenticate"),
        component: StepAuthenticate,
        forwardButton: t("Authenticate")
    },
    {
        title: t("Set up Authenticator"),
        component: StepShowQrCode,
        backButton: t("Back"),
        forwardButton: t("Next")
    },
    {
        title: t("Enter code"),
        component: StepChangePassword,
        backButton: t("Back"),
        forwardButton: t("Verify code")
    }
];

let stepsChangeAll = [
    {
        title: t("Please authenticate"),
        component: StepAuthenticate,
        forwardButton: t("Authenticate")
    },
    {
        title: t("Set up Authenticator"),
        component: StepShowQrCode,
        backButton: t("Back"),
        forwardButton: t("Next")
    },
    {
        title: t("Change password"),
        component: StepChangePassword,
        backButton: t("Back"),
        forwardButton: t("Change password"),
        props: {}
    }
];


/**
 * Password change.
 * Use as follows:
 *
 * a) When during login there were required to change password and/or set up two-factor authentication:
 *              <ResetPassword
 *                  type="session"
 *                  password={this.state.password}
 *                  clientId={this.props.location.query.client_id
 *                  redirectUrl={this.props.location.query.redirect_url}
 *                  nonce={this.props.location.query.state}
 *                  scope={this.props.location.query.scope}
 *                  onClose={this.onClose}
 *              />
 *    Component will use current session info.
 *
 * b) When change password required through forgot link:
 *              <ResetPassword type="forgot" authToken={this.props.params.authToken} onClose={this.onClose} />
 *    If authToken first character is "X" then both password change and auth key setup will be performed.
 *    Otherwise only password change will be performed.
 *
 * c) When manually requested password change:
 *    Component will require re-authentication with the password if needed
 *              <ResetPassword type="password" personId={action.data.id} personEmail={action.data.email} onClose={this.onClose} />
 *
 * d) When manually requested auth key change:
 *    Component will require re-authentication with the password if needed
 *              <ResetPassword type="authkey" personId={action.data.id} personEmail={action.data.email} onClose={this.onClose} />
 *
 * e) When manually requested both password and auth key change:
 *    Component will require re-authentication with the password if needed
 *              <ResetPassword type="both" personId={action.data.id} personEmail={action.data.email} onClose={this.onClose} />
 */
class ResetPasswordContainer extends React.Component {
    static propTypes = {
        /**
         * Password change type:
         *  a) "session" - password change request by session during login
         *  b) "forgot" - password change request by forgot link
         *  c) "password" - password change requested by already authenticated user
         *  d) "authkey" - auth code change requested by already authenticated user
         *  e) "both" - password and auth code change requested by already authenticated user
         */
        type: PropTypes.oneOf(["session", "forgot", "password", "authkey", "both"]).isRequired,
        /**
         * Person ID to change password from. If not specified then current user
         */
        personId: PropTypes.string,
        /**
         * Person email. If not specified then current  user's email
         */
        personEmail: PropTypes.string,
        /**
         * Current password
         */
        password: PropTypes.string,
        /**
         * User temporary session ID(in case when we have password reset link)
         */
        authToken: PropTypes.string,
        /**
         * OAuth2 client_id
         */
        clientId: PropTypes.string,
        /**
         * OAuth2 redirect_url
         */
        redirectUrl: PropTypes.string,
        /**
         * OAuth2 state
         */
        nonce: PropTypes.string,
        /**
         * OAuth2 scope
         */
        scope: PropTypes.string,

        /**
         * Called when user finishes or closes modal.
         */
        onClose: PropTypes.func.isRequired
    };

    static defaultProps = {
        type: "session"
    };

    constructor(props) {
        super(props);

        this.state = {
            steps: this.getSteps(props)
        };
    }

    componentWillReceiveProps(nextProps) {
        this.setState({steps: this.getSteps(nextProps)});
    }

    getSteps(props) {
        if (!props) props = this.props;
        let steps = null;
        switch (props.type) {
            case "session":
                let session = SessionStore.getState().currentRecord;
                steps = (session.person.requirePasswordChange ? (session.person.authKey ? stepsResetAll : stepsResetPassword) : stepsResetKey);
                break;
            case "forgot":
                switch (props.authToken.charAt(0)) {
                    case "X":
                        steps = stepsResetAll;
                        break;
                    case "T":
                    default:
                        steps = stepsResetPassword;
                }
                break;
            case "both":
                steps = stepsChangeAll;
                break;
            case "authkey":
                steps = stepsChangeKey;
                break;
            case "password":
            default:
                steps = stepsChangePassword;
                break;
        }

        let fixedSteps = [];
        for (let i = 0; i < steps.length; i++) {
            fixedSteps[i] = _.clone(steps[i]);
            fixedSteps[i].props = {
                personId: props.personId,
                personEmail: props.personEmail,
                password: props.password,
                authToken: props.authToken,
                clientId: props.clientId,
                redirectUrl: props.redirectUrl,
                nonce: props.nonce,
                scope: props.scope,
                type: props.type
            };

        }
        return fixedSteps;
    }

    render() {

        return (
            <ActionWizard steps={this.state.steps} allowClose={!this.props.type !== "session"} onClose={this.props.onClose} />
        );
    }
}

export default ResetPasswordContainer;
